import * as t from 'io-ts';
import _ from 'lodash';

import {isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {makeApiSerializableType, makeApiType, makeEnumRuntimeType} from './apiTypes';
import {RCalendarWeekDay} from './calendarCommonTypes';
import {ConferenceTypesEnum} from './conferenceModel';
import {RPaletteColor} from './paletteModelRuntime';
import {
  parseSchedulingLinkQuestion,
  RSchedulingLinkQuestion,
  serializeSchedulingLinkQuestion,
} from './schedulingLinkQuestionModel';

/*
 * Runtime type.
 */

export enum SchedulingLinkAvailabilityTypesEnum {
  STANDARD = 'standard',
  REPEATING = 'repeating',
}

export enum SchedulingLinkErrorTypesEnum {
  MISSING_INTEGRATION = 'missing_integration',
  INVALID_INTEGRATION_CREDENTIALS = 'invalid_integration_credentials',
  UNKNOWN = 'unknown',
}

export const RSchedulingLinkStandardAvailabilityBlock = t.type({
  start: t.number,
  end: t.number,
});

export const RSchedulingLinkRepeatingAvailabilityBlock = t.type({
  weekDay: RCalendarWeekDay,
  hour: t.number,
  minute: t.number,
  timezone: t.string,
  durationMinutes: t.number,
});

const RSchedulingLinkStandardAvailabilitySettings = t.type({
  type: t.literal(SchedulingLinkAvailabilityTypesEnum.STANDARD),
  blocks: t.readonlyArray(RSchedulingLinkStandardAvailabilityBlock),
});

const RSchedulingLinkRepeatingAvailabilitySettings = t.type({
  type: t.literal(SchedulingLinkAvailabilityTypesEnum.REPEATING),
  blocks: t.readonlyArray(RSchedulingLinkRepeatingAvailabilityBlock),
});

const RSchedulingLinkAvailabilitySettings = t.union([
  RSchedulingLinkStandardAvailabilitySettings,
  RSchedulingLinkRepeatingAvailabilitySettings,
]);

const RSchedulingLinkMeetingProperties = t.type({
  summary: t.string,
  durationMinutes: t.number,
  conferenceType: t.union([makeEnumRuntimeType(ConferenceTypesEnum), t.undefined]),
  location: t.string,
  description: t.string,
  additionalGuests: t.readonlyArray(t.string),
  questions: t.readonlyArray(RSchedulingLinkQuestion),
});

const RSchedulingLinkBase = t.type({
  slug: t.string,
  name: t.string,
  calendarVersionId: t.number,
  isShared: t.boolean,
  isOneTimeLink: t.boolean,
  isCalendarEventReschedulingLink: t.boolean,
  availability: RSchedulingLinkAvailabilitySettings,
  errorType: t.union([makeEnumRuntimeType(SchedulingLinkErrorTypesEnum), t.undefined]),
  meetingProperties: RSchedulingLinkMeetingProperties,
  updatedAt: t.number,
  displayName: t.string,
  greeting: t.string,
  bookingPageUrl: t.string,
  doubleBookingCalendarVersions: t.readonlyArray(t.number),
  startTimeIncrements: t.union([t.number, t.undefined]),
  meetingBufferMinutes: t.union([t.number, t.undefined]),
  advanceNoticeMinutes: t.union([t.number, t.undefined]),
  color: t.union([RPaletteColor, t.undefined]),
});

export const RSchedulingLink = t.intersection(
  [
    t.type({
      id: t.number,
    }),
    RSchedulingLinkBase,
  ],
  'schedulingLink',
);

export const RDraftSchedulingLink = t.intersection([
  t.type({
    id: t.union([t.number, t.undefined]),
  }),
  RSchedulingLinkBase,
]);

export const RSchedulingLinkAvailability = t.type({
  durationMinutes: t.number,
  timeSlots: t.readonlyArray(RSchedulingLinkStandardAvailabilityBlock),
});

/*
 * Type.
 */

export interface SchedulingLinkStandardAvailabilityBlockModel
  extends Readonly<t.TypeOf<typeof RSchedulingLinkStandardAvailabilityBlock>> {}
export interface SchedulingLinkRepeatingAvailabilityBlockModel
  extends Readonly<t.TypeOf<typeof RSchedulingLinkRepeatingAvailabilityBlock>> {}

interface SchedulingLinkStandardAvailabilitySettings
  extends Readonly<t.TypeOf<typeof RSchedulingLinkStandardAvailabilitySettings>> {}
interface SchedulingLinkRepeatingAvailabilitySettings
  extends Readonly<t.TypeOf<typeof RSchedulingLinkRepeatingAvailabilitySettings>> {}
export type SchedulingLinkAvailabilitySettings =
  | SchedulingLinkStandardAvailabilitySettings
  | SchedulingLinkRepeatingAvailabilitySettings;

export interface SchedulingLinkMeetingPropertiesModel
  extends Readonly<t.TypeOf<typeof RSchedulingLinkMeetingProperties>> {}

export interface SchedulingLinkModel extends Readonly<t.TypeOf<typeof RSchedulingLink>> {}
export interface DraftSchedulingLinkModel extends Readonly<t.TypeOf<typeof RDraftSchedulingLink>> {}

export interface SchedulingLinkAvailabilityModel
  extends Readonly<t.TypeOf<typeof RSchedulingLinkAvailability>> {}

/*
 * Helpers.
 */

export function isSavedSchedulingLink(link: DraftSchedulingLinkModel): link is SchedulingLinkModel {
  return link.id !== undefined;
}

/*
 * IO methods.
 */

export function parseSchedulingLink(src: any): SchedulingLinkModel {
  return {
    id: src.id,
    slug: src.slug,
    name: src.name || '',
    calendarVersionId: src.calendar_version_id,
    isShared: Boolean(src.is_shared),
    isOneTimeLink: Boolean(src.is_one_time_link),
    isCalendarEventReschedulingLink: Boolean(src.is_calendar_event_rescheduling_link),
    availability: parseSchedulingLinkAvailabilitySettings(src),
    errorType: parseSchedulingError(src.error_type),
    meetingProperties: src.meeting_properties && parseSchedulingLinkMeetingProperties(src.meeting_properties),
    updatedAt: src.updated_at,
    doubleBookingCalendarVersions: src.double_booking_calendar_versions || [],
    startTimeIncrements: src.start_time_increments || undefined,
    meetingBufferMinutes: src.meeting_buffer_minutes || undefined,
    advanceNoticeMinutes: src.advance_notice_minutes || undefined,
    displayName: src.display_name || '',
    greeting: src.greeting || '',
    bookingPageUrl: src.booking_page_url,
    color: RPaletteColor.is(src.color) ? src.color : undefined,
  };
}

function parseSchedulingLinkAvailabilitySettings(src: any): SchedulingLinkAvailabilitySettings {
  switch (src.availability_type) {
    case 'repeating':
      return {
        type: SchedulingLinkAvailabilityTypesEnum.REPEATING,
        blocks: src.repeating_availability_blocks.map(parseSchedulingLinkRepeatingAvailabilityBlock),
      };

    case 'standard':
    default:
      return {
        type: SchedulingLinkAvailabilityTypesEnum.STANDARD,
        blocks: src.standard_availability_blocks.map(parseSchedulingLinkStandardAvailabilityBlock),
      };
  }
}

function parseSchedulingError(src: any): SchedulingLinkErrorTypesEnum | undefined {
  if (!src) {
    return undefined;
  }

  switch (src) {
    case 'missing_integration':
      return SchedulingLinkErrorTypesEnum.MISSING_INTEGRATION;
    case 'invalid_integration_credentials':
      return SchedulingLinkErrorTypesEnum.INVALID_INTEGRATION_CREDENTIALS;
    default:
      return SchedulingLinkErrorTypesEnum.UNKNOWN;
  }
}

function parseSchedulingLinkMeetingProperties(src: any): SchedulingLinkMeetingPropertiesModel {
  return {
    summary: src.summary || '',
    durationMinutes: src.durationMinutes,
    conferenceType: src.conferenceType || undefined,
    location: src.location || '',
    description: src.description || '',
    additionalGuests: src.additionalGuests,
    questions: _(src.questions || [])
      .map(parseSchedulingLinkQuestion)
      .compact()
      .value(),
  };
}

function parseSchedulingLinkStandardAvailabilityBlock(
  src: any,
): SchedulingLinkStandardAvailabilityBlockModel {
  return {
    start: src.start,
    end: src.end,
  };
}

function parseSchedulingLinkRepeatingAvailabilityBlock(
  src: any,
): SchedulingLinkRepeatingAvailabilityBlockModel {
  return {
    weekDay: src.weekday.toLowerCase(),
    hour: src.hour,
    minute: src.minute,
    timezone: src.timezone,
    durationMinutes: src.durationMinutes,
  };
}

export function parseSchedulingLinkAvailability(src: any): SchedulingLinkAvailabilityModel {
  return {
    durationMinutes: src.duration_minutes,
    timeSlots: src.time_slots.map(parseSchedulingLinkStandardAvailabilityBlock),
  };
}

export function serializeSchedulingLink(link: SchedulingLinkModel | DraftSchedulingLinkModel) {
  return {
    id: link.id,
    slug: link.slug,
    name: link.name,
    calendar_version_id: link.calendarVersionId,
    is_shared: link.isShared,
    is_one_time_link: link.isOneTimeLink,
    is_calendar_event_rescheduling_link: link.isCalendarEventReschedulingLink,
    meeting_properties: serializeMeetingProperties(link.meetingProperties),
    double_booking_calendar_versions: link.doubleBookingCalendarVersions,
    availability_type: link.availability.type,
    repeating_availability_blocks:
      link.availability.type === SchedulingLinkAvailabilityTypesEnum.REPEATING
        ? serializeRepeatingAvailabilityBlocks(link.availability.blocks)
        : undefined,
    standard_availability_blocks:
      link.availability.type === SchedulingLinkAvailabilityTypesEnum.STANDARD
        ? serializeStandardAvailabilityBlocks(link.availability.blocks)
        : undefined,
    start_time_increments: link.startTimeIncrements,
    meeting_buffer_minutes: link.meetingBufferMinutes,
    advance_notice_minutes: link.advanceNoticeMinutes,
    display_name: link.displayName,
    greeting: link.greeting,
    color: link.color,
  };
}

function serializeMeetingProperties(properties: SchedulingLinkMeetingPropertiesModel) {
  return {
    summary: properties.summary,
    durationMinutes: properties.durationMinutes,
    location: properties.location,
    description: properties.description,
    additionalGuests: properties.additionalGuests,
    conferenceType: properties.conferenceType?.valueOf(),
    questions: properties.questions.map(serializeSchedulingLinkQuestion).filter(isDefined),
  };
}

function serializeRepeatingAvailabilityBlocks(
  availabilityBlocks: ReadonlyArray<SchedulingLinkRepeatingAvailabilityBlockModel>,
) {
  return availabilityBlocks.map((block) => ({
    weekday: _.capitalize(block.weekDay),
    hour: block.hour,
    minute: block.minute,
    timezone: block.timezone,
    durationMinutes: block.durationMinutes,
  }));
}

function serializeStandardAvailabilityBlocks(
  availabilityBlocks: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>,
) {
  return availabilityBlocks.map((block) => ({
    start: block.start,
    end: block.end,
  }));
}

/*
 * API type.
 */

export const ApiSchedulingLink = makeApiSerializableType(
  'schedulingLink',
  RSchedulingLink,
  parseSchedulingLink,
  serializeSchedulingLink,
);
export const ApiSchedulingLinkAvailability = makeApiType(
  'schedulingLinkAvailability',
  RSchedulingLinkAvailability,
  parseSchedulingLinkAvailability,
);
